import { groupBy, keyBy, orderBy } from "lodash-es"
import dayjs from "dayjs"
import { CrudFilter, HttpError, UseListProps, useCan, useInfiniteList, useList, useMany, useOne, useTranslation } from "@refinedev/core";
import { useSetAtom } from "jotai";
import { eventIdAtom } from "core/calendar/state";
import { calendar_v3 } from "googleapis"
import { EventCode, getEventCodeType, searchMetadataString } from "core/calendar/definitions";
import { logger } from "@utils/logger";
import { useAppCalendar } from "hooks/calendar/useAppCalendar";
import { FC, useMemo, useCallback } from "react";
import { AppointmentEvent } from "@components/atoms/AppointmentEvent";
import { User_Professional_Public, User_User_Public } from "@graphql/schema.types";
import { PRO_ROLES, USER_ROLES } from "access/definitions";
import React from "react";

interface CalendarEventsProps {
    minTime?: dayjs.Dayjs | Date | string,
    maxTime?: dayjs.Dayjs | Date | string
    pageSize?: number
}

type PopupComponent = {
    event: calendar_v3.Schema$Event,
    contact?: User_Professional_Public | User_User_Public
    name: 'user' | 'professional'
}

const toastCommonClasses = 'rounded-2xl gap-0 h-auto justify-center items-start lg:items-center flex flex-nowrap flex-col lg:flex-row py-2 px-4 lg:pr-8 min-h-12'

export const CalendarEvents: React.FC<CalendarEventsProps> = React.memo(({ minTime, maxTime, pageSize = 100 }: CalendarEventsProps) => {
    const calendarId = useAppCalendar()
    const { translate } = useTranslation()
    const setEventId = useSetAtom(eventIdAtom)
    logger.debug("calendarId", calendarId)

    const { data: isProfessional } = useCan({
        resource: "calendar",
        action: "list",
        params: { roles: PRO_ROLES }
    })

    const { data: isUser } = useCan({
        resource: "calendar",
        action: "list",
        params: { roles: USER_ROLES }
    })
    logger.debug("auth", isUser, isProfessional)

    // Memoize the base params to prevent object recreation
    const baseParams = useMemo((): UseListProps<calendar_v3.Schema$Event, HttpError, calendar_v3.Schema$Event> => ({
        dataProviderName: 'gapi',
        resource: `calendar/calendars/:calendar/events`, //https://developers.google.com/calendar/api/v3/reference/events/list
        sorters: [
            {
                field: 'startTime',
                order: 'asc'
            }
        ],
        pagination: {
            pageSize: pageSize,
        },
        filters: [
            (minTime && {
                field: 'timeMin',
                operator: 'eq',
                value: dayjs(minTime).toISOString()
            }) as CrudFilter,
            (maxTime && {
                field: 'timeMax',
                operator: 'eq',
                value: dayjs(maxTime).toISOString()
            }) as CrudFilter,
            {
                field: 'showDeleted',
                operator: 'eq',
                value: false
            },
            {
                field: 'singleEvents',
                operator: 'eq',
                value: true
            },
            {
                field: 'q',
                operator: 'eq',
                value: searchMetadataString({})
            }
        ]
    }), [minTime, maxTime, pageSize])

    const {
        data: availabilityEvents,
        // isError,
        // isLoading,
        // hasNextPage,
        // fetchNextPage,
        // isFetchingNextPage,
    } = useList<calendar_v3.Schema$Event, HttpError, calendar_v3.Schema$Event>({
        ...baseParams,
        meta: {
            params: {
                calendar: calendarId?.availability
            }
        },
        queryOptions: {
            enabled: !!calendarId?.availability
        },
    })

    const {
        data: appointmentEvents,
        // isError,
        // isLoading,
        // hasNextPage,
        // fetchNextPage,
        // isFetchingNextPage,
    } = useList<calendar_v3.Schema$Event, HttpError, calendar_v3.Schema$Event>({
        ...baseParams,
        meta: {
            params: {
                calendar: calendarId?.appointments
            }
        },
        queryOptions: {
            enabled: !!calendarId?.appointments
        },
    })

    const eventColors: Record<EventCode | 'default', { button?: string, text?: string, component?: FC<PopupComponent> }> = {
        'default': {
            button: 'btn-ghost hover:btn-primary'
        },
        [EventCode.APPOINTMENT]: {
            component: AppointmentToast
        },
        [EventCode.AVAILABILITY]: {
            button: 'btn-secondary btn-outline hover:btn-primary striped-thin from-secondary/30 to-transparent',
            text: 'bg-base-100'
        },
        [EventCode.EMERGENCY_CALL]: {
            component: EmergencyToast
        }
    }

    // Memoize events array concatenation
    const events = useMemo(() => [
        ...(availabilityEvents?.data ? availabilityEvents?.data : []),
        ...(appointmentEvents?.data ? appointmentEvents?.data : []),
    ], [availabilityEvents?.data, appointmentEvents?.data])

    // Memoize professional emails set
    const proEmails = useMemo(() => 
        new Set(isUser?.can ? events.map(event => event.creator?.email) : []),
        [isUser?.can, events]
    )

    const { data: professionals } = useList<User_Professional_Public, HttpError, User_Professional_Public>({
        resource: 'user_professional_public',
        filters: [
            {
                field: 'email',
                operator: 'in',
                value: [...proEmails]
            }
        ],
        queryOptions: {
            enabled: proEmails.size > 0
        },
        meta: {
            fields: [
                'email',
                'avatar_url',
                'display_name',
            ],
        }
    })

    // Memoize professionals by email
    const professionalsByEmail = useMemo(() => 
        keyBy(professionals?.data ?? [], 'email'),
        [professionals?.data]
    )
    logger.debug("professionals by email", professionalsByEmail)

    // Memoize user emails set
    const userEmails = useMemo(() => 
        new Set(isProfessional?.can ? events.flatMap(event => event.attendees?.map(a => a.email)).filter(e => !!e) : []),
        [isProfessional?.can, events]
    )
    logger.debug(userEmails)

    const { data: users } = useList<User_Professional_Public, HttpError, User_Professional_Public>({
        resource: 'user_user_public',
        filters: [
            {
                field: 'email',
                operator: 'in',
                value: [...userEmails]
            }
        ],
        queryOptions: {
            enabled: userEmails.size > 0
        },
        meta: {
            fields: [
                'email',
                'avatar_url',
                'display_name',
            ],
        }
    })

    // Memoize users by email
    const usersByEmail = useMemo(() => 
        keyBy(users?.data ?? [], 'email'),
        [users?.data]
    )
    logger.debug("users by email", usersByEmail)

    // Memoize events grouping and sorting
    const { eventsGp, sortedDates } = useMemo(() => {
        const eventsGp = groupBy(events ?? [], (event) => dayjs(event.start?.date ?? event.start?.dateTime).format("YYYY-MM-DD"))
        const sortedDates = orderBy(Object.keys(eventsGp), date => dayjs(date).valueOf())
        return { eventsGp, sortedDates }
    }, [events])

    logger.debug("sortedDates", sortedDates, availabilityEvents, appointmentEvents)

    // Memoize the click handler
    const handleEventClick = useCallback((eventId: string) => {
        setEventId(eventId)
    }, [setEventId])

    return sortedDates.length > 0 ? sortedDates.map((date, i) => <div key={i} className='flex gap-6 my-4 w-full'>
        <div className="w-8 lg:w-16 flex flex-col items-center">
            <span className='text-xs uppercase '>{dayjs(date).format("ddd")}</span>
            <div className={`${dayjs(date).format("YYYY-MM-DD") == dayjs().format("YYYY-MM-DD") ? "rounded-full w-10 h-10 bg-primary" : "-mt-1"} font-semibold text-lg flex items-center justify-center`}>{dayjs(date).date()}</div>
        </div>
        <div className={`flex-1 flex flex-col gap-1 ${eventsGp[date].length == 1 ? 'self-center' : ''}`}>
            {eventsGp[date].map(event => {
                // logger.debug("calendar event", event)
                const eventType = getEventCodeType(event.description ?? '') ?? 'default'
                const classes = eventColors[eventType]

                const professionalEmail = event?.creator?.email  //event?.attendees?.find(a => a?.email?.includes('gserviceaccount'))?.email
                const professional = professionalsByEmail[professionalEmail ?? '']
                // logger.debug(professionalEmail, professional)

                const userEmail = event?.attendees?.find(a => !a?.email?.includes('gserviceaccount'))?.email
                const user = usersByEmail[userEmail ?? '']
                // logger.debug(userEmail, user)

                return classes.component ?
                    <React.Fragment key={event.id}>
                        {isUser?.can ? <classes.component
                            key={event.id + (professionalEmail ?? '')}
                            event={event}
                            contact={professional}
                            name='professional'
                        /> : null}
                        {isProfessional?.can ? <classes.component
                            key={event.id + (userEmail ?? '')}
                            event={event}
                            contact={user}
                            name='user'
                        /> : null}
                    </React.Fragment>
                    : <div
                        key={event.id}
                        className={`btn ${classes.button ?? ''} ${toastCommonClasses}`}
                        onClick={() => {
                            if (eventType == EventCode.AVAILABILITY) {
                                handleEventClick(event.id!)
                            }
                        }}
                    >
                        <div className="flex-1 flex">
                            <span className={`text-start text-sm font-semibold normal-case px-2 rounded-full ${classes.text ?? ''}`}>{event.summary}</span>
                        </div>
                        {event.start?.dateTime && <span className={`text-sm font-normal px-2 rounded-full ${classes.text ?? ''}`}>{dayjs(event.start?.dateTime).format("HH:mm")} - {dayjs(event.end?.dateTime).format("HH:mm")}</span>}
                    </div>
            })}
        </div>
    </div>) : <span className="text-xl my-4">{translate("page.calendar.no-events")}</span>
})

const AppointmentToast = React.memo(({ event, contact, name, ...rest }: PopupComponent) => {
    return <div className="dropdown" {...rest}>
        <div tabIndex={0} role="button" className={`btn btn-secondary hover:btn-primary ${toastCommonClasses}`}>
            <div className="flex-1 flex">
                <span className={`text-start text-sm font-semibold normal-case px-2 rounded-full`}>{event.summary}</span>
            </div>
            {event.start?.dateTime && <span className={`text-sm font-normal px-2 rounded-full`}>{dayjs(event.start?.dateTime).format("HH:mm")} - {dayjs(event.end?.dateTime).format("HH:mm")}</span>}
        </div>
        <AppointmentEvent
            tabIndex={0}
            className="dropdown-content z-10 mt-1"
            event={event}
            contact={contact}
            name={name}
        />
    </div >
})

const EmergencyToast = React.memo(({ event, contact, name, ...rest }: PopupComponent) => {
    return <div className="dropdown" {...rest}>
        <div tabIndex={0} role="button" className={`btn btn-error bg-red-300 ${toastCommonClasses}`}>
            <div className="flex-1 flex">
                <span className={`text-start text-sm font-semibold normal-case px-2 rounded-full`}>{event.summary}</span>
            </div>
            {event.start?.dateTime && <span className={`text-sm font-normal px-2 rounded-full`}>{dayjs(event.start?.dateTime).format("HH:mm")} - {dayjs(event.end?.dateTime).format("HH:mm")}</span>}
        </div>
        <AppointmentEvent
            tabIndex={0}
            className="dropdown-content z-10 mt-1"
            event={event}
            contact={contact}
            name={name}
        />
    </div >
})